<template>
  <b-modal id="edit-modal" size="lg" title="Edit Card Bundle">
    <div id="edit-package" class="modal-form">
      <b-row>
        <b-col sm="12" md="3">
          <h6 class="main-label">Title</h6>
        </b-col>
        <b-col>
          <b-form-group
            id="titleGroup"
            label="Card Bundle Title"
            label-for="title"
            class="secondary-label mb-2"
            :invalid-feedback="errorMessage('title')"
            :state="isValid('title')"
          >
            <b-form-input id="title" type="text" v-model="form.title" required maxlength="25"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col>
          <hr class="form-divider" />
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12" md="3">
          <h6 class="main-label">Description</h6>
        </b-col>
        <b-col>
          <b-form-group
            id="descriptionGroup"
            label="About this Card Bundle"
            label-for="description"
            class="secondary-label mb-2"
            :invalid-feedback="errorMessage('description')"
            :state="isValid('description')"
          >
            <b-form-textarea
              id="description"
              :label="'About this Card Bundle ' + descriptionLabel"
              v-model="form.description"
              placeholder="Enter description"
              :rows="3"
              :maxlength="descriptionMaxLength"
              :max-rows="6"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col>
          <hr class="form-divider" />
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12" md="3">
          <h6 class="main-label">Price</h6>
        </b-col>
        <b-col cols="5">
          <div id="package-price-container">
            <b-form-group
              id="currencyGroup"
              label="Card Bundle Price"
              label-for="currency"
              class="secondary-label"
              :invalid-feedback="errorMessage('price')"
              :state="isValid('price')"
            >
              <b-input-group>
                <b-form-select slot="prepend" :options="currencyOptions" v-model="form.currency"></b-form-select>
                <b-form-input
                  id="packagePrice"
                  v-model="form.price"
                  required
                  type="number"
                  min="1"
                  max="10000000000"
                  oninput="this.value = Math.abs(this.value)"
                />
              </b-input-group>
            </b-form-group>
          </div>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col>
          <hr class="form-divider" />
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12" md="3">
          <h6 class="main-label">Validity</h6>
        </b-col>
        <b-col>
          <b-row>
            <b-col cols="4">
              <b-form-group
                id="durationGroup"
                label="Duration"
                label-for="duration"
                class="secondary-label"
                :invalid-feedback="errorMessage('validity_duration')"
                :state="isValid('validity_duration')"
              >
                <b-form-input
                  id="duration"
                  type="number"
                  v-model="form.validity_duration"
                  required
                  min="1"
                  max="99"
                  oninput="this.value = Math.abs(this.value)"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col class="pl-0">
              <b-form-group
                id="daysGroup"
                label-for="days"
                class="secondary-label validity-unit"
                :invalid-feedback="errorMessage('validity_type')"
                :state="isValid('validity_type')"
              >
                <b-form-select v-model="form.validity_type" :options="durationTypeOptions" />
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row class="mb-2 d-none">
        <b-col>
          <hr class="form-divider" />
        </b-col>
      </b-row>
      <b-row class="d-none">
        <b-col sm="12" md="3">
          <h6 class="main-label">Discount</h6>
        </b-col>
        <b-col>
          <b-row no-gutters>
            <b-col cols="5">
              <div id="discount-price-container">
                <b-form-group
                  id="discountGroup"
                  label="Early Bird Discount"
                  label-for="discount"
                  class="secondary-label"
                >
                  <b-input-group>
                    <b-form-select
                      slot="prepend"
                      :options="discountOptions"
                      v-model="form.discount_type"
                      @input="discountInputChecker()"
                    ></b-form-select>
                    <b-form-input
                      v-model="form.discount"
                      required
                      type="number"
                      min="1"
                      max="99"
                      :formatter="discountFormatter"
                    />
                  </b-input-group>
                </b-form-group>
              </div>
            </b-col>
            <b-col></b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group
                class="mb-1 secondary-label"
                :invalid-feedback="errorMessage('discount_start_date')"
                :state="isValid('discount_start_date')"
              >
                <label class="secondary-label align-top">Start Date</label>
                <div class="custom-datepicker mb-3 muli-sb">
                  <SingleDatepicker
                    :bootstrap-styling="true"
                    :value="form.discount_start_date"
                    ref="programaticOpenStartDate"
                    v-model="form.discount_start_date"
                    wrapper-class="custom-datepicker"
                    style="color: #2a2a2a !important;"
                  >
                    <div slot="afterDateInput" class="input-group-append pr-0">
                      <span class="input-group-text">
                        <img
                          @click="openPicker('startDate')"
                          src="@/assets/images/icons/calendar.svg"
                          alt="Calendar Icon"
                          class="form-icon action-button"
                        />
                      </span>
                    </div>
                  </SingleDatepicker>
                </div>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                class="mb-1 secondary-label"
                :invalid-feedback="errorMessage('discount_end_date')"
                :state="isValid('discount_end_date')"
              >
                <label class="secondary-label align-top">End Date</label>
                <div class="custom-datepicker mb-3 muli-sb">
                  <SingleDatepicker
                    :bootstrap-styling="true"
                    :value="form.discount_end_date"
                    ref="programaticOpenEndDate"
                    v-model="form.discount_end_date"
                    wrapper-class="custom-datepicker"
                    style="color: #2a2a2a !important;"
                  >
                    <div slot="afterDateInput" class="input-group-append pr-0">
                      <span class="input-group-text">
                        <img
                          @click="openPicker('endDate')"
                          src="@/assets/images/icons/calendar.svg"
                          alt="Calendar Icon"
                          class="form-icon action-button"
                        />
                      </span>
                    </div>
                  </SingleDatepicker>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col>
          <hr class="form-divider" />
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12" md="3">
          <h6 class="main-label">Card Bundle Content</h6>
        </b-col>
        <b-col>
          <b-form-group
            label="Vouchers"
            label-for="vouchers"
            label-class="secondary-label"
            class="position-relative"
            id="vouchersGroup"
          >
            <b-input-group>
              <b-input-group
                slot="prepend"
                class="border rounded-left px-2 d-flex align-items-center"
              >
                <template v-if="is_search">
                  <i class="ic-search"></i>
                </template>
                <template v-else>
                  <b-button variant=" btn-remove shadow-none" @click="hideVoucherList"></b-button>
                </template>
              </b-input-group>
              <b-form-input
                v-model="voucher_search"
                :placeholder="form.vouchers.length + ' Vouchers Selected'"
                @focus.native="showVoucherList"
                autocomplete="off"
              ></b-form-input>
            </b-input-group>
            <div
              id="voucher-list"
              ref="voucherList"
              class="position-absolute bg-white w-100 d-none"
            >
              <ul class="p-0 m-0 d-flex flex-column">
                <template v-if="loading">
                  <li>Loading Vouchers...</li>
                </template>
                <template v-else>
                  <b-form-checkbox-group v-model="form.vouchers">
                    <li
                      v-for="(voucher, index) in voucherOptions"
                      :key="index"
                      :id="'voucher-edit-' + index"
                      class="d-flex align-items-center border-bottom"
                    >
                      <span class="flex-grow-1">{{ voucher.text }}</span>
                      <b-form-checkbox
                        name="notifyme"
                        :value="voucher.value"
                        unchecked-value="0"
                        class="mt-2 mx-0 mb-0 p-0"
                      ></b-form-checkbox>
                    </li>
                  </b-form-checkbox-group>
                </template>
                <li class="action">
                  <b-button
                    type="button"
                    variant="default unselect-users w-100 py-2"
                    @click="unselectVouchers"
                  >Unselect All</b-button>
                </li>
              </ul>
            </div>
          </b-form-group>
          <b-form-group
            label="Rewards"
            label-for="rewards"
            label-class="secondary-label"
            class="position-relative"
            id="rewardsGroup"
          >
            <b-input-group>
              <b-input-group
                slot="prepend"
                class="border rounded-left px-2 d-flex align-items-center"
              >
                <template v-if="is_rewards_search">
                  <i class="ic-search"></i>
                </template>
                <template v-else>
                  <b-button variant=" btn-remove shadow-none" @click="hideRewardList"></b-button>
                </template>
              </b-input-group>
              <b-form-input
                v-model="reward_search"
                :placeholder="form.rewards.length + ' Rewards Selected'"
                @focus.native="showRewardList"
                autocomplete="off"
              ></b-form-input>
            </b-input-group>

            <div id="reward-list" ref="rewardList" class="position-absolute bg-white w-100 d-none">
              <ul class="p-0 m-0 d-flex flex-column">
                <template v-if="loading">
                  <li>Loading Rewards...</li>
                </template>
                <template v-else>
                  <b-form-checkbox-group v-model="form.rewards">
                    <li
                      v-for="(reward, index) in rewardOptions"
                      :key="index"
                      :id="'reward-edit-' + index"
                      class="d-flex align-items-center border-bottom"
                    >
                      <span class="flex-grow-1">{{ reward.text }}</span>
                      <b-form-checkbox
                        name="notifyme"
                        :value="reward.value"
                        unchecked-value="0"
                        class="mt-2 mx-0 mb-0 p-0"
                      ></b-form-checkbox>
                    </li>
                  </b-form-checkbox-group>
                </template>
                <li class="action">
                  <b-button
                    type="button"
                    variant="default unselect-users w-100 py-2"
                    @click="unselectRewards"
                  >Unselect All</b-button>
                </li>
              </ul>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col>
          <hr class="form-divider" />
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12" md="3">
          <h6 class="main-label">Image Assets</h6>
          <span class="label-helper">(200x200)</span>
        </b-col>
        <b-col>
          <div id="image-assets-wrapper" :style="{ 'color': this.form.label_text }">
            <h3 class="mb-3">Package Card</h3>
            <h3 class="mb-3">Preview</h3>
            <div class="card mb-4" :style="[cardStyleObject]">
              <b-row no-gutters>
                <b-col>
                  <div class="profile-details align-top">
                    <p class="mb-1 name">Juan Dela Cruz</p>
                    <p class="mb-1 card-number">1234 5678 9101 1123</p>
                    <p class="mb-1 validity">valid until 1 Jan 2019</p>
                  </div>
                </b-col>
              </b-row>
              <b-row no-gutters class="mt-2">
                <b-col>
                  <div class="package-details align-top pt-2">
                    <p class="mb-1">Package</p>
                    <p class="mb-1">Package Title</p>
                  </div>
                  <div class="package-details-img">
                    <img v-if="logoStamp === ''" :src="form.logo" alt="Package Image" style="object-fit: contain;" />
                    <img v-else :src="logoStamp" alt="Package Image" style="object-fit: contain;" />
                  </div>
                </b-col>
              </b-row>
            </div>
            <b-form-group
              label="Card Background (Optional - 525 x 300 Recommended Size)"
              class="secondary-label"
              :invalid-feedback="errorMessage('card')"
              :state="isValid('card')"
            >
              <b-form-file
                v-model="card"
                :placeholder="form.card_placeholder ? form.card_placeholder : 'Select .jpg, .png or .gif'"
                accept="image/jpeg, image/png, image/gif"
                class="select-file"
                :file-name-formatter="formatNames"
                @input="updateCardStyle()"
              ></b-form-file>
            </b-form-group>
            <b-row no-gutters>
              <b-col cols="4">
                <b-form-group
                  label="Background Color"
                  id="color-picker"
                  class="secondary-label"
                  :description="form.card_background"
                >
                  <b-form-input v-model="form.card_background" type="color"></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="1">
                <img
                  src="@/assets/images/icons/colorpicker.svg"
                  alt="Color Picker Icon"
                  id="color-picker-icon"
                />
              </b-col>
            </b-row>
            <b-form-group label="Label Text" class="secondary-label" id="label-text-group">
              <b-form-radio-group v-model="form.label_text">
                <b-form-radio value="#ffffff">
                  <span id="first-text-option" class="label-text-option">Aa</span>
                </b-form-radio>
                <b-form-radio value="#000000">
                  <span id="second-text-option" class="label-text-option">Aa</span>
                </b-form-radio>
              </b-form-radio-group>
            </b-form-group>
          </div>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col>
          <hr class="form-divider" />
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12" md="3">
          <h6 class="main-label">Visibility</h6>
        </b-col>
        <b-col>
          <div>
            <switches
              v-model="form.visibility"
              theme="custom"
              color="primary"
              type-bold="true"
              text-enabled="On"
              text-disabled="Off"
              style="top: -18px;"
            ></switches>
          </div>
        </b-col>
      </b-row>
    </div>
    <div slot="modal-footer">
      <b-button
        variant="ghost"
        v-bind:disabled="loading"
        class="btn-cancel"
        style="margin-right: .25rem;"
        @click="clearFields"
      >Cancel</b-button>
      <b-button
        variant="primary"
        v-bind:disabled="loading"
        style="margin-left: .25rem;"
        @click="sendForm"
      >
        <template v-if="loading">Saving...</template>
        <template v-else>Save</template>
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapState } from "vuex";
import Switches from "vue-switches";
import SingleDatepicker from "vuejs-datepicker";

export default {
    name: "editPurchasePackage",
    props: ["logoStamp", "form"],
    data() {
        return {
            currencyOptions: [{ value: "PHP", text: "PHP" }],
            discountOptions: [
                { value: "%", text: "%" },
                { value: "PHP", text: "PHP" }
            ],
            durationTypeOptions: [
                { value: "days", text: "Days" },
                { value: "weeks", text: "Weeks" },
                { value: "months", text: "Months" },
                { value: "years", text: "Years" }
            ],
            voucher_search: "",
            reward_search: "",
            is_search: true,
            is_rewards_search: true,
            card: null,
            cardBackgroundPreview: null,
            descriptionMaxLength: 300
        };
    },
    computed: {
        ...mapState("purchasePackages", [
            "loading",
            "errors",
            "voucherOptions",
            "rewardOptions"
        ]),
        descriptionLabel: {
            get: function() {
                if (this.form.description != null) {
                    return (
                        this.form.description.length +
                        " / " +
                        this.descriptionMaxLength
                    );
                }
                return "0 / " + this.descriptionMaxLength;
            }
        },
        cardStyleObject() {
            let cardBackground = null;

            if (this.cardBackgroundPreview) {
                cardBackground = this.cardBackgroundPreview;
            } else {
                cardBackground = this.form.card;
            }

            return {
                background:
                    this.form.card_background +
                    " url(" +
                    cardBackground +
                    ") no-repeat center"
            };
        }
    },
    components: {
        Switches,
        SingleDatepicker
    },
    methods: {
        async getPurchasePackages(page) {
            let param = {
                page: page,
                payload: this.searchPayload
            };
            await this.$store.dispatch(
                "purchasePackages/getPurchasePackages",
                param
            );
        },
        async getTopPurchasePackages() {
            await this.$store.dispatch(
                "purchasePackages/getTopPurchasePackages"
            );
        },
        async sendForm() {
            this.$store.dispatch("purchasePackages/resetErrors");

            let startDate =
                this.$moment(this.form.discount_start_date).format(
                    "YYYY-MM-DD"
                ) + " 00:00:00";
            let endDate =
                this.$moment(this.form.discount_end_date).format("YYYY-MM-DD") +
                " 23:59:59";

            let cardInBase64 = null;

            if (this.card) {
                cardInBase64 = await this.getBase64(this.card);
            }

            let data = {
                uuid: this.form.uuid,
                param: {
                    title: this.form.title,
                    card_background: this.form.card_background,
                    label_text: this.form.label_text,
                    description: this.form.description,
                    validity_duration: this.form.validity_duration,
                    validity_type: this.form.validity_type,
                    price: this.form.price,
                    currency: this.form.currency,
                    visibility: this.form.visibility,
                    discount: this.form.discount,
                    discount_type: this.form.discount_type,
                    discount_start_date: startDate,
                    discount_end_date: endDate,
                    voucher_uuid: null,
                    vouchers: JSON.stringify(this.form.vouchers),
                    rewards: JSON.stringify(this.form.rewards)
                }
            };

            if (this.logoStamp !== "") {
                data.param.logo = this.logoStamp.replace(
                    /^data:image\/\w+;base64,/,
                    ""
                );
            }

            if (cardInBase64) {
                data.param.card = cardInBase64.replace(
                    /^data:image\/\w+;base64,/,
                    ""
                );
            }

            if (this.card != null) {
                if (this.card.size / 1024 / 1024 > 4) {
                    this.$store.dispatch("purchasePackages/fileIsTooBigError");
                } else {
                    let _this = this;
                    await this.$store
                        .dispatch(
                            "purchasePackages/updatePurchasePackage",
                            data
                        )
                        .then(function() {
                            if (_this.errors == null) {
                                _this.clearFields();
                                _this.hideModal("edit-modal");
                                _this.getPurchasePackages(1);
                                _this.getTopPurchasePackages();
                            }
                        });
                }
            } else {
                let _this = this;
                await this.$store
                    .dispatch("purchasePackages/updatePurchasePackage", data)
                    .then(function() {
                        if (_this.errors == null) {
                            _this.clearFields();
                            _this.hideModal("edit-modal");
                            _this.getPurchasePackages(1);
                            _this.getTopPurchasePackages();
                        }
                    });
            }
        },
        errorMessage(field) {
            if (this.errors == null) {
                return "";
            }

            return this.errors.errors[field];
        },
        isValid(field) {
            if (this.errors == null) {
                return false;
            }
            return this.errors.errors[field] ? false : true;
        },
        clearFields() {
            this.hideModal("edit-modal");
            this.$store.dispatch("purchasePackages/resetErrors");
            this.$emit("clearImageLogoStamp", true);
        },
        showModal(modal) {
            this.$root.$emit("bv::show::modal", modal);
        },
        hideModal(modal) {
            this.$root.$emit("bv::hide::modal", modal);
        },
        openPicker(dateInputField) {
            if (dateInputField == "startDate") {
                this.$refs.programaticOpenStartDate.showCalendar();
            } else {
                this.$refs.programaticOpenEndDate.showCalendar();
            }
        },
        discountInputChecker() {
            if (this.form.discount_type === "%") {
                if (this.form.discount > 99) {
                    this.form.discount = 99;
                }
            } else {
                if (this.form.discount > this.form.price) {
                    this.form.discount = this.form.price;
                }
            }
        },
        discountFormatter(value, event) {
            if (event === null) {
                return value;
            }

            if (this.form.discount_type === "%") {
                if (value > 99) {
                    return 99;
                }
            } else {
                if (Number(value) > this.form.price) {
                    return this.form.price;
                }
            }
            return value;
        },
        formatNames(files) {
            if (files.length === 1) {
                return files[0].name;
            } else {
                return `${files.length} files selected`;
            }
        },
        async getBase64(file, onLoadCallback) {
            return new Promise(function(resolve, reject) {
                const reader = new FileReader();
                reader.onload = function() {
                    resolve(reader.result);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },
        showVoucherList: function() {
            this.is_search = false;
            this.$refs.voucherList.classList.remove("d-none");
        },
        hideVoucherList: function() {
            this.is_search = true;
            this.$refs.voucherList.classList.add("d-none");
        },
        unselectVouchers: function() {
            this.form.vouchers = [];
        },
        addVouchers: async function() {
            await this.selected_voucher.filter(voucher => {
                this.getVoucher(voucher);
            });

            this.$refs.voucherList.classList.add("d-none");
            this.voucher_search = "";
            this.selected_voucher = [];
            this.is_search = true;
        },
        showRewardList: function() {
            this.is_rewards_search = false;
            this.$refs.rewardList.classList.remove("d-none");
        },
        hideRewardList: function() {
            this.is_rewards_search = true;
            this.$refs.rewardList.classList.add("d-none");
        },
        unselectRewards: function() {
            this.form.reward = [];
        },
        addRewards: async function() {
            await this.selected_reward.filter(reward => {
                this.getReward(reward);
            });

            this.$refs.rewardList.classList.add("d-none");
            this.is_rewards_search = "";
            this.selected_reward = [];
            this.is_rewards_search = true;
        },
        updateCardStyle() {
            const file = this.card;
            this.cardBackgroundPreview = file
                ? URL.createObjectURL(file)
                : null;
            console.log(this.cardBackgroundPreview);
        }
    },
    watch: {
      reward_search: function (str) {
        str = str.toLowerCase().trim();
        this.rewardOptions.forEach(function (reward, i) {
          if (str !== '') {
            if (reward.text.toLowerCase().includes(str)) {
              document.getElementById("reward-edit-" + i).classList.remove("d-none");
            } else {
              document.getElementById("reward-edit-" + i).classList.add("d-none");
            }
          } else {
            document.getElementById("reward-edit-" + i).classList.remove("d-none");
          }
        });
      },
      voucher_search: function (str) {
        str = str.toLowerCase().trim();
        this.voucherOptions.forEach(function (voucher, i) {
          if (str !== '') {
            if (voucher.text.toLowerCase().includes(str)) {
              document.getElementById("voucher-edit-" + i).classList.remove("d-none");
            } else {
              document.getElementById("voucher-edit-" + i).classList.add("d-none");
            }
          } else {
            document.getElementById("voucher-edit-" + i).classList.remove("d-none");
          }
        });
      }
    }
};
</script>
<style scoped>
.d-none {
  display: none !important;
}
</style>